import { attrsToBuilderProps } from '@/components/tiptap-editor/tiptap-extension/short-answer-extension/tool-settings/lib/assessment-attrs';
import { renderTableColgroupHtml } from '@/components/tiptap-editor/tiptap-extension/zytable-extension/utils';
import { normalizeMatrixRows } from '@/lib/zymatrix-utils';
import { flattenExtensions, type Extensions, type JSONContent } from '@tiptap/core';
import type { ZyServers } from '../servers-core';
import { stripListItemParagraph, stripOuterParagraphs } from '../tiptap-dom-html-utils';
import { alphabetEnumerate, escapeXml, formatIndentedTextForHtml, generateImgTag, toTitleCase } from '../utils';
import {
    getDescendantOfType,
    isZyVersionNodeVisible,
    normalizeImportedImageSrc,
    normalizeVersion,
    renderJsonTo,
    type SelectedZyVersionFilter,
} from '../zy-tiptap-utils-core';
import { createZybooksApi, type ZybooksApi } from '../zybooks-api-core';
import { buildExportAssessmentFields } from './node-parser/short-answer-assessment-export';
import { buildZyITLabPayload } from './node-parser/zyitlab-export';

export type { SelectedZyVersionFilter };

const CONTENT_RESOURCE_TEMPLATE = {
    id: '',
    activity_type: null,
    caption: null,
    gdoc_id: '',
    height: null,
    instructions: null,
    optional: null,
    parent_gdoc_id: null,
    parts: 0,
    property_overrides: null,
    release_id: '',
    width: null,
    type: '',
    payload: {},
};

const DEFAULT_SECTION_EXPORT_SUBJECT = 'default_chapter';
const DEFAULT_SECTION_EXPORT_RELEASE_ID = '1';

interface ProcessNodeOptions {
    skipGuidGeneration?: boolean;
    processingChildNodes?: boolean;
}

interface EnumTreeNode {
    [enumId: string]: string;
}

interface EnumTree {
    [chapterNumber: string]: {
        [sectionNumber: string]: EnumTreeNode;
    };
}

interface InlineEnumInfo {
    chapter_number: number;
    letter: string;
    section_number: number;
}

interface InlineEnumMap {
    [enumId: string]: InlineEnumInfo;
}

export interface ReferenceToContentResourcesEnumMaps {
    inline_enums_lettering_map?: EnumTree;
    inline_enum_map?: InlineEnumMap;
}

type ParticipationActivityQuestionType = 'multiple_choice' | 'short_answer' | 'detect_answer' | 'custom';

interface ShortAnswerQuestion {
    text: zybookHTMLContent[];
    explanation: zybookHTMLContent[];
    hint: string;
    render_html: boolean;
    text_before?: string | null;
    text_after?: string | null;
    rows?: string;
    columns?: string;
    obey_case?: boolean;
    obey_space?: boolean;
    obey_quote?: boolean;
    obey_linear?: boolean;
    preferred?: string | null;
    answers: string[];
    ignoreend?: string;
    assessment_type?: string;
    notation?: string;
    tolerance?: string;
    width?: string | number;
    absolute_value?: boolean;
    require_sig_fig?: boolean;
    require_n_or_fewer_sig_figs?: number;
    placeholder?: string;
    accept_E_notation?: boolean;
    accept_scientific_notation?: boolean;
    hide_E_notation?: boolean;
    give_units?: boolean;
    physical_quantities?: string;
    preferred_units?: string;
    require_engineering_notation?: boolean;
    require_strict_scientific_notation?: boolean;
    require_units?: boolean;
    unit_prefixes?: string;
    unitless_option?: string;
    boolean_functions?: string;
    boolean_variables?: string;
    correctness_methods?: string;
    editor_atomic_symbols?: string;
    operators?: string;
    support_true_and_false?: boolean;
    untyped_variables?: string;
    accepted_substitute_variables?: string;
    expected_functions?: string;
    expected_variables?: string;
    tests?: string;
    virtual_keyboard_layout_name?: string;
    functions_variables?: string;
    language?: string;
}

interface zybookHTMLContent {
    text: string;
    guid?: string;
    reference?: string;
    ref?: string;
    enumid?: string;
    attributes?: Array<{
        type: string;
        [key: string]: any;
    }>;
}

export type ConvertProseMirrorToZybooksJSONDeps = {
    getExtensions: () => Extensions;
    servers: ZyServers;
};

export type ConvertContext = {
    deps: ConvertProseMirrorToZybooksJSONDeps;
    api: ZybooksApi;
    nodeTypesInSchema: string[];
};

type NodeProcessor = (
    node: JSONContent,
    docAttr: Record<string, any> | undefined,
    ctx: ConvertContext,
) => Record<string, any> | Promise<Record<string, any>>;

function renderNodeHtml(node: JSONContent, deps: ConvertProseMirrorToZybooksJSONDeps): string {
    return renderJsonTo(node, deps.getExtensions(), 'html');
}

export interface ConvertProseMirrorToZybooksJSONOptions {
    isMAPreview?: boolean;
    zybookCode?: string | null;
    selectedZyVersion?: SelectedZyVersionFilter;
    skipGuidGeneration?: boolean;
    preserveCommentAnnotations?: boolean;
}

interface MultipleChoiceQuestion {
    text: zybookHTMLContent[];
    choices: {
        label: string;
        explanation: zybookHTMLContent[];
        correct: boolean;
    }[];
}

interface DetectAnswerOption {
    text: string;
    correct: boolean;
    explanation: string;
}

interface DetectAnswerQuestion {
    contents: Array<
        | {
              type: 'text';
              text: string;
          }
        | {
              type: 'button';
              text: string;
              correct: boolean;
              explanation: string;
          }
    >;
}

function extractDetectAnswerExplanationText(explanationNode?: JSONContent): string {
    if (!explanationNode || !Array.isArray(explanationNode.content)) {
        return '';
    }

    return explanationNode.content.map(extractPlainText).join('').trim();
}

interface IndexItem {
    keyword: zybookHTMLContent[];
    type: string;
    context: zybookHTMLContent[];
}

const REFERENCE_PLACEHOLDER_PREFIX = '__ZY_REFERENCE_';

/**
 * Builds the zyBooks html segment for a reference node. `reference` is the target resource id,
 * which imported references carry as `importTargetId` and MA-authored references carry as `nodeId`.
 */
const referenceHtmlContent = (node: JSONContent): zybookHTMLContent => ({
    guid: node.attrs?.id ?? '',
    text: '',
    reference: node.attrs?.importTargetId ?? node.attrs?.nodeId ?? '',
    attributes: [{ type: 'reference' }],
});

/**
 * Replaces every reference descendant with a text placeholder so the surrounding markup can still be
 * rendered as one HTML string, then split back apart around the references.
 *
 * @param node - Node to copy.
 * @param references - Collects the reference segments in placeholder order.
 */
const replaceReferencesWithPlaceholders = (node: JSONContent, references: zybookHTMLContent[]): JSONContent => {
    if (!node.content) {
        return node;
    }

    return {
        ...node,
        content: node.content.map(child => {
            if (child.type === 'reference') {
                const placeholder = `${REFERENCE_PLACEHOLDER_PREFIX}${references.length}__`;
                references.push(referenceHtmlContent(child));
                return { type: 'text', text: placeholder };
            }
            return replaceReferencesWithPlaceholders(child, references);
        }),
    };
};

const splitHtmlOnReferencePlaceholders = (html: string, references: zybookHTMLContent[]): zybookHTMLContent[] => {
    const segments: zybookHTMLContent[] = [];
    const placeholderPattern = new RegExp(`${REFERENCE_PLACEHOLDER_PREFIX}(\\d+)__`, 'g');
    let sliceStart = 0;

    for (let match = placeholderPattern.exec(html); match !== null; match = placeholderPattern.exec(html)) {
        if (match.index > sliceStart) {
            segments.push({ attributes: [{ type: 'html' }], text: html.slice(sliceStart, match.index) });
        }
        segments.push(references[Number(match[1])]);
        sliceStart = match.index + match[0].length;
    }

    if (sliceStart < html.length) {
        segments.push({ attributes: [{ type: 'html' }], text: html.slice(sliceStart) });
    }

    return segments;
};

const renderHtmlSegments = (node: JSONContent, ctx: ConvertContext): zybookHTMLContent[] => {
    const references: zybookHTMLContent[] = [];
    const html = renderNodeHtml(replaceReferencesWithPlaceholders(node, references), ctx.deps);

    if (references.length === 0) {
        return [{ attributes: [{ type: 'html' }], text: html }];
    }

    return splitHtmlOnReferencePlaceholders(html, references);
};

const processReference = (node: JSONContent, docAttr: Record<string, any> | undefined): Record<string, any> => ({
    ...CONTENT_RESOURCE_TEMPLATE,
    id: node.attrs?.id,
    type: 'html',
    payload: {
        html: [referenceHtmlContent(node)],
        image_ids: [],
        subject: docAttr?.attrs?.subjectName ?? DEFAULT_SECTION_EXPORT_SUBJECT,
        ref: node.attrs?.id,
    },
});

// Generic HTML processor
const processHTML = (node: JSONContent, docAttr: Record<string, any> | undefined, ctx: ConvertContext): Record<string, any> => {
    let index_items: IndexItem[] = [];

    // Check for zydefn or zyterm nodes to add to index_items
    if (node.type === 'paragraph' || node.type === 'zydefnBlock') {
        index_items = [...extractZyDefnIndexItems(node, ctx), ...extractZyTermIndexItems(node)];
    }

    return {
        ...CONTENT_RESOURCE_TEMPLATE,
        id: node.attrs?.id,
        type: 'html',
        index_items,
        payload: {
            html: renderHtmlSegments(node, ctx),
            image_ids: [],
            subject: docAttr?.attrs?.subjectName ?? DEFAULT_SECTION_EXPORT_SUBJECT,
            ref: node.attrs?.id,
        },
    };
};

const processIframe = (node: JSONContent, docAttr: Record<string, any> | undefined, ctx: ConvertContext): Record<string, any> => {
    const attrs = node.attrs || {};
    if (attrs.title !== 'Video') {
        return processHTML(node, docAttr, ctx);
    }

    return {
        ...CONTENT_RESOURCE_TEMPLATE,
        id: node.attrs?.id,
        type: 'html',
        payload: {
            html: [
                {
                    src: attrs.src || '',
                    title: attrs.title || 'Video',
                    width: attrs.width == null ? '' : String(attrs.width),
                    height: attrs.height == null ? '' : String(attrs.height),
                    sandbox: attrs.sandbox || '',
                    attributes: [{ type: 'iframe' }],
                    wiley_hosted: attrs.wileyHosted === true || attrs.wileyHosted === 'true',
                },
            ],
            image_ids: [],
            subject: docAttr?.attrs?.subjectName ?? DEFAULT_SECTION_EXPORT_SUBJECT,
            ref: node.attrs?.id,
        },
    };
};

/**
 * Use instead of processHTML when the node's children can include custom nodes (e.g. zyInlineEnum):
 * processes each child with the right processor so they become proper content items, not one HTML string.
 */
const processHTMLWithCustomNode = async (
    node: JSONContent,
    docAttr: Record<string, any> | undefined,
    ctx: ConvertContext,
): Promise<Record<string, any>> => {
    let index_items: IndexItem[] = [];

    // Check for zydefn or zyterm nodes to add to index_items
    if (node.type === 'paragraph' || node.type === 'listItem') {
        index_items = [...extractZyDefnIndexItems(node, ctx), ...extractZyTermIndexItems(node)];
    }

    return {
        ...CONTENT_RESOURCE_TEMPLATE,
        id: node.attrs?.id,
        type: 'html',
        index_items,
        payload: {
            html: await processChildNodes(node.content || [], docAttr, ctx),
            image_ids: [],
            subject: docAttr?.attrs?.subjectName ?? DEFAULT_SECTION_EXPORT_SUBJECT,
            ref: node.attrs?.id,
        },
    };
};

const parseZytoolsImageSrc = (src: string, docAttr?: Record<string, any>): { subject: string; releaseId: string; imageId: string } => {
    const match = typeof src === 'string' ? src.match(/\/zyAuthor\/([^/]+)\/([^/]+)\/IMAGES\/([^/?#]+)/) : null;
    if (match) {
        return {
            subject: docAttr?.attrs?.subjectName ?? match[1],
            releaseId: String(docAttr?.attrs?.releaseId ?? match[2]),
            imageId: match[3],
        };
    }

    const imageId =
        (typeof src === 'string' ? src.split('IMAGES/')[1]?.split(/[?#]/)[0] : '') ||
        (typeof src === 'string' ? src.split('/').pop()?.split(/[?#]/)[0] : '') ||
        '';
    return {
        subject: docAttr?.attrs?.subjectName ?? DEFAULT_SECTION_EXPORT_SUBJECT,
        releaseId: String(docAttr?.attrs?.releaseId ?? DEFAULT_SECTION_EXPORT_RELEASE_ID),
        imageId,
    };
};

const processImageFromZytools = (node: JSONContent, docAttr: Record<string, any> | undefined): Record<string, any> => {
    const attrs = node.attrs || {};
    const { subject, releaseId, imageId } = parseZytoolsImageSrc(attrs.src || '', docAttr);
    const pull = attrs?.pull === 'left' || attrs?.pull === 'right' ? attrs.pull : null;

    const imageAttributes = {
        subject: subject,
        filename: imageId,
        release_id: releaseId,
        image_ids: [imageId],
        title: attrs?.title || null,
        alt_text: attrs?.alt || null,
        height: attrs?.height || null,
        type: 'image',
        ...(attrs?.width != null ? { width: String(attrs.width) } : {}),
        ...(pull ? { pull } : {}),
    };

    if (pull) {
        return {
            ...CONTENT_RESOURCE_TEMPLATE,
            id: node.attrs?.id,
            type: 'html',
            payload: {
                html: [
                    {
                        text: generateImgTag({ text: '', attributes: [imageAttributes] }, { subject, releaseId }),
                        // Keep image_ids so parent containers (e.g. zyFigure) can collect them.
                        attributes: [{ type: 'html', image_ids: [imageId] }],
                    },
                ],
                image_ids: [],
                subject,
                ref: node.attrs?.id,
            },
        };
    }

    return {
        ...CONTENT_RESOURCE_TEMPLATE,
        id: node.attrs?.id,
        type: 'html',
        payload: {
            html: [
                {
                    text: null,
                    attributes: [imageAttributes],
                },
            ],
            image_ids: [],
            subject,
            ref: node.attrs?.id,
        },
    };
};

const processChildImage = (node: JSONContent, docAttr: Record<string, any> | undefined, ctx: ConvertContext): Record<string, any> => {
    const attrs = node.attrs || {};

    if (attrs.src?.includes('zytools.zybooks.com') && !attrs.renderAsHTML) {
        return processImageFromZytools(node, docAttr);
    }

    const normalizedSrc = normalizeImportedImageSrc(attrs, docAttr?.attrs);
    return processHTML({ ...node, attrs: { ...attrs, src: normalizedSrc } }, docAttr, ctx);
};

const processImageNode = (node: JSONContent, docAttr: Record<string, any> | undefined): Record<string, any> => {
    const { attrs } = node;

    const altText = attrs?.alt;

    const imageId = attrs?.id;
    const width = attrs?.width == null ? null : String(attrs.width);
    const { subject, releaseId, imageId: imageFilename } = parseZytoolsImageSrc(attrs?.src || '', docAttr);
    const title = attrs?.title || null;

    return {
        ...CONTENT_RESOURCE_TEMPLATE,
        id: imageId,
        payload: {
            subject,
            alt_text: altText,
            filename: imageFilename,
            title: title,
            image_ids: [imageFilename],
            ...(width != null ? { width } : {}),
        },
        type: 'image',
        subject,
        release_id: releaseId,
    };
};

const processZyGdocDrawing = async (
    node: JSONContent,
    docAttr: Record<string, any> | undefined,
    ctx: ConvertContext,
): Promise<Record<string, any>> => {
    const { attrs } = node;

    const altText = attrs?.alt_text;
    if (!altText) {
        throw new Error('Alt text is required for zyGdocDrawing');
    }

    const gdocDrawingId = attrs?.id;
    const width = attrs?.width || null;
    const height = attrs?.height || null;
    const pull = attrs?.pull || null;
    const subject = docAttr?.attrs?.subjectName ?? DEFAULT_SECTION_EXPORT_SUBJECT;
    const releaseID = String(docAttr?.attrs?.releaseId ?? DEFAULT_SECTION_EXPORT_RELEASE_ID);
    const gdocFileId = attrs?.gdoc_file_id || '';

    let imageIdFromS3 = '';
    if (gdocFileId && subject && releaseID) {
        const { key, last_modified } = await ctx.api.uploadGdocDrawingToS3(
            gdocFileId,
            gdocDrawingId,
            subject,
            releaseID,
            attrs.gdoc_modified_time,
        );
        imageIdFromS3 = key.split('/').pop() || '';
        attrs.image_id = imageIdFromS3;
        attrs.gdoc_modified_time = last_modified;
    }

    return {
        ...CONTENT_RESOURCE_TEMPLATE,
        id: gdocDrawingId,
        payload: {
            width: width,
            height: height,
            subject,
            alt_text: altText,
            filename: imageIdFromS3,
            image_ids: [imageIdFromS3],
            pull: pull,
            ...(width != null ? { width } : {}),
        },
        type: 'image',
        subject,
    };
};

const extractZyDefnIndexItems = (node: JSONContent, ctx: ConvertContext): IndexItem[] => {
    const index_items: IndexItem[] = [];
    const zyDefnNodes = [...getDescendantOfType(node, 'zydefn'), ...getDescendantOfType(node, 'zydefnBlock')];

    zyDefnNodes.forEach((zyDefnNode: JSONContent) => {
        const termNodes = getDescendantOfType(zyDefnNode, 'zydefTerm');
        const terms = Array.from(
            new Set(
                termNodes
                    .map(extractPlainText)
                    .map(t => t.trim())
                    .filter(Boolean),
            ),
        );

        if (terms.length === 0) {
            return;
        }

        const stripTermNodes = (nodes: JSONContent[]): JSONContent[] =>
            nodes.flatMap(n => {
                if (n.type === 'zydefTerm') {
                    return n.content || [];
                }
                if (n.content) {
                    return [{ ...n, content: stripTermNodes(n.content) }];
                }
                return [n];
            });

        const cleanedContextText = (() => {
            const strippedContent = stripTermNodes(zyDefnNode.content || []);
            if (zyDefnNode.type === 'zydefn') {
                const contextText = renderNodeHtml({ type: 'paragraph', content: strippedContent }, ctx.deps);
                return contextText.replace(/^<p>/, '').replace(/<\/p>$/, '');
            }

            return strippedContent.map(n => renderNodeHtml(n, ctx.deps)).join('');
        })();
        terms.forEach((term: string) => {
            index_items.push({
                keyword: [
                    {
                        text: term,
                        attributes: [{ type: 'html' }],
                    },
                ],
                type: 'definition',
                context: [
                    {
                        text: cleanedContextText,
                        attributes: [{ type: 'html' }],
                    },
                ],
            });
        });
    });
    return index_items;
};

const extractPlainText = (n: JSONContent): string => {
    if (n.type === 'text') {
        return n.text || '';
    }
    if (!n.content) {
        return '';
    }
    return n.content.map(extractPlainText).join('');
};

const extractZyTermIndexItems = (node: JSONContent): IndexItem[] => {
    const zyTermNodes = getDescendantOfType(node, 'zyTerm');
    return zyTermNodes
        .map((n): IndexItem | null => {
            const text = extractPlainText(n).trim();
            if (!text) return null;
            const keyword = [{ text, attributes: [{ type: 'html' }] }];
            return { keyword, type: 'term', context: [] as zybookHTMLContent[] };
        })
        .filter((item): item is IndexItem => item !== null);
};

const processMultipleChoiceQuestion = async (mcNode: JSONContent, docAttr: Record<string, any> | undefined, ctx: ConvertContext) => {
    const mcContent = mcNode.content || [];
    let questionText: zybookHTMLContent[] = [];
    const choices: MultipleChoiceQuestion['choices'] = [];

    for (const item of mcContent) {
        if (item.type === 'multipleChoiceQuestion') {
            questionText = await processChildNodes(item.content || [], docAttr, ctx);
        } else if (item.type === 'multipleChoiceChoice') {
            const labelNodes: JSONContent[] = [];
            const explanationNodes: JSONContent[] = [];
            item.content?.forEach(n => {
                if (n.type === 'multipleChoiceExplanation') {
                    explanationNodes.push(n);
                } else {
                    labelNodes.push(n);
                }
            });

            choices.push({
                label: (await processChildNodes(labelNodes, docAttr, ctx)).map(n => n.text).join(''),
                explanation: await processChildNodes(explanationNodes, docAttr, ctx),
                correct: item.attrs?.isCorrect || false,
            });
        }
    }

    return {
        text: questionText,
        choices,
    };
};

const processShortAnswerQuestion = async (
    saNode: JSONContent,
    docAttr: Record<string, any> | undefined,
    ctx: ConvertContext,
): Promise<ShortAnswerQuestion> => {
    const saContent = saNode.content || [];

    let text: zybookHTMLContent[] = [];
    let explanation: zybookHTMLContent[] = [];
    let hint = '';
    let answers: string[] = [];
    let preferred: string | null = null;
    let textBefore: string | null = null;
    let textAfter: string | null = null;

    let answerNode: JSONContent | null = null;
    for (const item of saContent) {
        if (item.type === 'shortAnswerQuestion') {
            // Process question content to HTML
            const questionContent = item.content || [];
            text = await processChildNodes(questionContent, docAttr, ctx);
        } else if (item.type === 'shortAnswerAnswer') {
            answerNode = item;
            const answerList = item.attrs?.answers || [];
            answers = Array.isArray(answerList) ? answerList : [];
            const preferred_index = item.attrs?.preferredIndex;
            if (typeof preferred_index === 'number' && preferred_index >= 0 && preferred_index < answers.length) {
                preferred = answers[preferred_index] ?? null;
            }
        } else if (item.type === 'shortAnswerAnswerWrapper') {
            //
            // Process answer wrapper to extract text_before and text_after
            //
            // Replace {{ANSWER}} with XXANSWERXX to avoid colorization issues
            // Future enhancement: use answer from answerNode for more accurate colorization
            //
            const answerPlaceholder = 'XXANSWERXX';
            const wrapperContent = (item.attrs?.content || '{{ANSWER}}').replace('{{ANSWER}}', answerPlaceholder);
            const language = item.attrs?.language || 'plaintext';

            const colorizedContent = await ctx.api.colorizeCode(wrapperContent, language);
            const parts = colorizedContent.split(answerPlaceholder);

            if (parts.length > 1) {
                // Remove trailing `<span class="n">` from pre_text (matches parser.py logic)
                let preText = parts[0];
                if (preText.endsWith('<span class="n">')) {
                    preText = preText.slice(0, -16);
                }

                // Remove leading `</span>` from post_text (matches parser.py logic)
                let postText = parts[1];
                if (postText.startsWith('</span>')) {
                    postText = postText.slice(7);
                }

                // Fix classes added by zyServer colorization
                preText = preText.replace('highlight code', 'highlight');

                textBefore = preText;
                textAfter = postText;
            } else {
                // If no placeholder found, put everything in text_before
                textBefore = colorizedContent;
                textAfter = '';
            }
        } else if (item.type === 'shortAnswerExplanation') {
            // Process explanation content to HTML
            const explanationContent = item.content || [];
            explanation = await processChildNodes(explanationContent, docAttr, ctx);
        } else if (item.type === 'shortAnswerHint') {
            hint = renderNodeHtml(item, ctx.deps);
        }
    }

    const question: ShortAnswerQuestion = {
        text,
        explanation,
        hint,
        render_html: true,
        answers,
    };

    // Extract additional attributes from the node if present
    if (answerNode?.attrs) {
        const bp = attrsToBuilderProps(answerNode.attrs as Record<string, unknown>);
        const isStringMatch = (bp.assessment_type ?? 'string') === 'string';

        if (isStringMatch) {
            question.text_before = textBefore;
            question.text_after = textAfter;
            question.rows = bp.lines !== undefined && bp.lines !== null ? String(bp.lines) : '1';
            question.columns = bp.columns !== undefined && bp.columns !== null ? String(bp.columns) : '20';
            question.obey_case = bp.obeycase !== undefined ? Boolean(bp.obeycase) : false;
            question.obey_space = bp.obeyspace !== undefined ? Boolean(bp.obeyspace) : false;
            question.obey_quote = bp.obeyquote !== undefined ? Boolean(bp.obeyquote) : true;
            question.obey_linear = bp.obeylinear !== undefined ? Boolean(bp.obeylinear) : false;
            question.preferred = preferred;
        }

        const extraAssessment = buildExportAssessmentFields(answerNode.attrs as Record<string, unknown>);
        Object.assign(question as unknown as Record<string, unknown>, extraAssessment);

        const ignoreTail =
            (typeof bp.ignoreend === 'string' && bp.ignoreend) ||
            (typeof extraAssessment.ignoreend === 'string' && extraAssessment.ignoreend) ||
            '';
        if (ignoreTail) {
            question.answers = question.answers.concat(question.answers.map(answer => answer + ignoreTail));
        }
    }

    return question;
};

function flattenDetectAnswerFragments(
    nodes: JSONContent[],
    inheritedOptionId: string | null = null,
): Array<{ type: 'text' | 'button'; text: string; optionId?: string }> {
    const fragments: Array<{ type: 'text' | 'button'; text: string; optionId?: string }> = [];

    nodes.forEach(node => {
        if (node.type === 'text' && typeof node.text === 'string') {
            const markOptionId =
                node.marks?.find(mark => mark.type === 'detectAnswerOptionMark' && typeof mark.attrs?.optionId === 'string')?.attrs
                    ?.optionId || inheritedOptionId;

            if (markOptionId) {
                fragments.push({ type: 'button', text: node.text, optionId: markOptionId });
            } else {
                fragments.push({ type: 'text', text: node.text });
            }
            return;
        }

        if (node.type === 'hardBreak') {
            fragments.push({ type: 'text', text: '\n' });
            return;
        }

        if (Array.isArray(node.content) && node.content.length > 0) {
            const nested = flattenDetectAnswerFragments(node.content, inheritedOptionId);
            fragments.push(...nested);

            if (['paragraph', 'heading', 'listItem'].includes(node.type || '')) {
                fragments.push({ type: 'text', text: '\n' });
            }
        }
    });

    return fragments;
}

function mergeDetectAnswerFragments(fragments: Array<{ type: 'text' | 'button'; text: string; optionId?: string }>) {
    const merged: Array<{ type: 'text' | 'button'; text: string; optionId?: string }> = [];

    for (let index = 0; index < fragments.length; index++) {
        const fragment = fragments[index];
        if (!fragment.text) {
            continue;
        }

        const previous = merged[merged.length - 1];
        if (!previous) {
            merged.push(fragment);
            continue;
        }

        const next = fragments[index + 1];
        if (
            fragment.type === 'text' &&
            fragment.text === '\n' &&
            previous.type === 'button' &&
            next?.type === 'button' &&
            previous.optionId &&
            previous.optionId === next.optionId
        ) {
            previous.text += '\n' + next.text;
            index++;
            continue;
        }

        const sameType = previous.type === fragment.type;
        const sameOption = previous.optionId === fragment.optionId;
        if (sameType && (fragment.type === 'text' || sameOption)) {
            previous.text += fragment.text;
            continue;
        }

        merged.push(fragment);
    }

    return merged;
}

const processDetectAnswerQuestion = async (daNode: JSONContent): Promise<DetectAnswerQuestion> => {
    const daContent = daNode.content || [];
    const optionById = new Map<string, DetectAnswerOption>();
    const orderedOptionIds: string[] = [];
    let questionContentNodes: JSONContent[] = [];

    for (const item of daContent) {
        if (item.type === 'detectAnswerQuestion') {
            questionContentNodes = item.content || [];
            continue;
        }

        if (item.type === 'detectAnswerOption') {
            const optionId = item.attrs?.optionId || '';
            if (!optionId) {
                continue;
            }

            const explanationNode = item.content?.find(child => child.type === 'detectAnswerExplanation');
            const explanation = extractDetectAnswerExplanationText(explanationNode);

            optionById.set(optionId, {
                text: item.attrs?.text || '',
                correct: item.attrs?.isCorrect || false,
                explanation,
            });
            orderedOptionIds.push(optionId);
        }
    }

    const flattened = flattenDetectAnswerFragments(questionContentNodes);
    let merged = mergeDetectAnswerFragments(flattened);

    const hasButtonFragments = merged.some(fragment => fragment.type === 'button');
    // Fallback for legacy/inconsistent states where options exist but marks were not persisted.
    if (!hasButtonFragments && orderedOptionIds.length > 0) {
        const linked: Array<{ type: 'text' | 'button'; text: string; optionId?: string }> = [...merged];

        for (const optionId of orderedOptionIds) {
            const option = optionById.get(optionId);
            const optionText = option?.text?.trim();
            if (!option || !optionText) {
                continue;
            }

            const targetIndex = linked.findIndex(fragment => fragment.type === 'text' && fragment.text.includes(optionText));
            if (targetIndex < 0) {
                continue;
            }

            const target = linked[targetIndex];
            const matchIndex = target.text.indexOf(optionText);
            const before = target.text.slice(0, matchIndex);
            const after = target.text.slice(matchIndex + optionText.length);

            const replacement: Array<{ type: 'text' | 'button'; text: string; optionId?: string }> = [];
            if (before) {
                replacement.push({ type: 'text', text: before });
            }
            replacement.push({ type: 'button', text: optionText, optionId });
            if (after) {
                replacement.push({ type: 'text', text: after });
            }

            linked.splice(targetIndex, 1, ...replacement);
        }

        merged = mergeDetectAnswerFragments(linked);
    }

    const contents = merged
        .map(fragment => {
            if (fragment.type === 'text') {
                return {
                    type: 'text' as const,
                    text: formatIndentedTextForHtml(escapeXml(fragment.text)),
                };
            }

            const option = fragment.optionId ? optionById.get(fragment.optionId) : null;
            return {
                type: 'button' as const,
                text: formatIndentedTextForHtml(escapeXml(option?.text || fragment.text)),
                correct: option?.correct || false,
                explanation: option?.explanation || '',
            };
        })
        .filter(item => item.text.length > 0);

    return { contents };
};

const processMatching = (node: JSONContent, docAttr: Record<string, any> | undefined, ctx: ConvertContext): Record<string, any> => {
    const content = node.content || [];
    const terms: Array<{
        term: string;
        definition: string;
        explanation: string;
    }> = [];

    content.forEach(pairNode => {
        if (pairNode.type === 'matchingPair') {
            const pairContent = pairNode.content || [];

            let term = '';
            let definition = '';
            let explanation = '';

            // Extract term, definition, and explanation from nested content
            pairContent.forEach(childNode => {
                if (childNode.type === 'matchingPairTerm') {
                    // Process term content to HTML
                    const termContent = childNode.content || [];
                    term = stripOuterParagraphs(termContent.map(n => renderNodeHtml(n, ctx.deps)).join(''));
                } else if (childNode.type === 'matchingPairDefinition') {
                    // Process definition content to HTML
                    const definitionContent = childNode.content || [];
                    definition = stripOuterParagraphs(definitionContent.map(n => renderNodeHtml(n, ctx.deps)).join(''));
                } else if (childNode.type === 'matchingPairExplanation') {
                    // Process explanation content to HTML
                    const explanationContent = childNode.content || [];
                    explanation = stripOuterParagraphs(explanationContent.map(n => renderNodeHtml(n, ctx.deps)).join(''));
                }
            });

            terms.push({
                term,
                definition,
                explanation,
            });
        }
    });

    return {
        payload: {
            tool: 'defnMatch',
            options: {
                terms,
            },
            image_ids: [],
            subject: docAttr?.attrs?.subjectName ?? DEFAULT_SECTION_EXPORT_SUBJECT,
        },
    };
};

/**
 * Process zyAnimator node and convert to zyBooks JSON format
 */
const processZyAnimator = (node: JSONContent, docAttr: Record<string, any> | undefined): Record<string, any> => {
    const attrs = node.attrs || {};

    const animatorId = attrs.id;
    const animXML = attrs.animXMLToLoad || '';
    const caption = attrs.caption || 'Animation';
    const height = attrs.height || '350px';
    const width = attrs.width || '600px';
    const alt_text = attrs.alt_text || '';
    const image_ids = attrs.image_ids || [];

    // Extract numeric values from height and width strings
    const heightNum = parseInt(height);
    const widthNum = parseInt(width);

    return {
        ...CONTENT_RESOURCE_TEMPLATE,
        id: animatorId,
        activity_type: 'participation',
        type: 'custom',
        parts: 1,
        caption: caption,
        width: widthNum,
        height: heightNum,
        payload: {
            tool: 'zyAnimator',
            subject: docAttr?.attrs?.subjectName ?? DEFAULT_SECTION_EXPORT_SUBJECT,
            options: {
                animXMLToLoad: animXML,
            },
            alt_text: alt_text,
            image_ids: image_ids,
        },
    };
};

const processZyYoutubeVideo = (node: JSONContent): Record<string, any> => {
    const { attrs } = node;
    const caption = attrs?.caption || '';
    const videoId = attrs?.id;
    let link = attrs?.link || '';
    // The following modifications were taken directly from the parser
    // Turn off related videos
    if (!link.includes('?rel=0')) {
        link += '?rel=0';
    }

    // Remove trailing &feature=youtu.be from link
    const suffix = '&feature=youtu.be';
    if (link.endsWith(suffix)) {
        link = link.slice(0, -suffix.length);
    }

    return {
        ...CONTENT_RESOURCE_TEMPLATE,
        id: videoId,
        type: 'video',
        parts: 0,
        caption: caption,
        payload: {
            link: link,
        },
    };
};

const processQuestionSet = async (
    node: JSONContent,
    docAttr: Record<string, any> | undefined,
    ctx: ConvertContext,
): Promise<Record<string, any>> => {
    const content = node.content || [];
    const activityType = node.attrs?.activityType || 'participation';

    // Extract instructions and activity nodes
    const questions: (MultipleChoiceQuestion | ShortAnswerQuestion | DetectAnswerQuestion)[] = [];
    const instructions: zybookHTMLContent[] = [];
    let questionType: ParticipationActivityQuestionType | null = null;

    for (let i = 0; i < content.length; i++) {
        const childNode = content[i];
        if (childNode.type === 'multipleChoice') {
            questions.push(await processMultipleChoiceQuestion(childNode, docAttr, ctx));
            questionType = 'multiple_choice';
        } else if (childNode.type === 'shortAnswer') {
            questions.push(await processShortAnswerQuestion(childNode, docAttr, ctx));
            questionType = 'short_answer';
        } else if (childNode.type === 'detectAnswer') {
            questions.push(await processDetectAnswerQuestion(childNode));
            questionType = 'detect_answer';
        } else if (childNode.type === 'matching') {
            const matchingResult = processMatching(childNode, docAttr, ctx);
            return {
                ...CONTENT_RESOURCE_TEMPLATE,
                id: node.attrs?.id || '',
                type: 'custom',
                parts: questions.length,
                activity_type: activityType,
                caption: node.attrs?.caption || 'Matching question',
                instructions: instructions.length > 0 ? instructions : null,
                ...matchingResult,
            };
        } else {
            instructions.push(...(await processChildNodes([childNode], docAttr, ctx)));
        }
    }

    return {
        ...CONTENT_RESOURCE_TEMPLATE,
        id: node.attrs?.id || '',
        type: questionType || '',
        parts: questions.length,
        activity_type: activityType,
        caption: node.attrs?.caption || '',
        instructions: instructions.length > 0 ? instructions : null,
        payload: {
            questions,
            image_ids: [],
            subject: docAttr?.attrs?.subjectName ?? DEFAULT_SECTION_EXPORT_SUBJECT,
        },
    };
};

/**
 * Process zyTool node and convert to zyBooks JSON format
 */
const processZyTool = (node: JSONContent, docAttr: Record<string, any> | undefined): Record<string, any> => {
    const { attrs } = node;
    const toolId = attrs?.id;
    const toolName = attrs?.name || '';
    const captionText = attrs?.caption || '';
    const toolHeight = attrs?.height ? `${attrs.height}px` : null;
    const toolWidth = attrs?.width ? `${attrs.width}px` : null;
    const toolOptions = attrs?.options || {};
    const instructions = attrs?.instructions || '';
    const challenge = attrs?.challenge || false;
    const parts = attrs?.parts || 1;
    const optional = attrs?.optional || false;
    const image_ids = attrs?.image_ids || [];
    const alt_text = attrs?.alt_text || '';

    return {
        ...CONTENT_RESOURCE_TEMPLATE,
        activity_type: challenge ? 'challenge' : 'participation',
        type: 'custom',
        parts: parts,
        id: toolId,
        caption: captionText,
        width: toolWidth,
        height: toolHeight,
        instructions: instructions,
        payload: {
            tool: toolName,
            subject: docAttr?.attrs?.subjectName ?? DEFAULT_SECTION_EXPORT_SUBJECT,
            options: toolOptions,
            optional: optional,
            alt_text: alt_text,
            image_ids: image_ids,
        },
    };
};

const processMathWorksLearningTool = async (
    node: JSONContent,
    docAttr: Record<string, any> | undefined,
    ctx: ConvertContext,
): Promise<Record<string, any>> => {
    const { attrs } = node;
    const payload: Record<string, any> = {
        provider_name: attrs?.providerName || 'mathworks_learning_tool',
    };

    if (attrs?.ltiResourceLinkId) {
        payload.resource_link_id = attrs.ltiResourceLinkId;
    }

    // Generate launch data during preview
    if (docAttr?.isMAPreview && attrs?.ltiResourceLinkId) {
        try {
            const response = await ctx.api.generateLtiLaunchData({
                providerName: attrs?.providerName || 'mathworks_learning_tool',
                resourceLinkId: attrs.ltiResourceLinkId,
                zybookCode: docAttr?.zybookCode ?? null,
                contentResourceId: attrs?.id,
            });

            payload.launch_data = response.launch_data ?? payload.launch_data;
            payload.launch_url = response.launch_url ?? payload.launch_url;
        } catch (error) {
            console.warn('Unable to generate MathWorks preview launch data as Learner.', error);
        }
    }

    return {
        ...CONTENT_RESOURCE_TEMPLATE,
        activity_type: 'challenge',
        content_resource_type: 'lti',
        type: 'lti',
        parts: attrs?.parts || 1,
        id: attrs?.id,
        caption: attrs?.caption || '',
        payload,
    };
};

function buildLegacyMatrixHtml(label: string, matrixSuper: string, matrixCenter: boolean, parsedRows: string[][]): string {
    let html = '';
    if (matrixCenter) {
        html += '<center>';
    }

    html += `
        <!-- begin matrix --><table class="table_matrix" style="border: 0;">
            <tbody>
                <tr>
                    <td style="border: 0px">${escapeXml(label)}</td>
                    <td style="border: 0px; padding: 0px;">
                        <table cellpadding="0" cellspacing="0" border="0" style="border-top: 0; border-bottom: 0; border-left: 1px solid #000; border-right: 1px solid #000">
                            <tbody>
                                <tr>
                                    <td style="border-top: 1px solid #000; border-bottom: 1px solid #000; padding: 0px; border-right: 0px; border-left: 0px;">&nbsp;</td>
                                    <td style="border: 0px;">
                                        <table cellpadding="0" cellspacing="0" border="0" style="border: 0;">
                                            <tbody>
    `;

    parsedRows.forEach(row => {
        html += '<tr>';
        row.forEach(cell => {
            const textAlign = matrixCenter ? 'center' : 'left';
            html += `
                <td align="center" valign="center" style="padding-left: 5px; padding-right: 5px; padding-top: 3px; padding-bottom: 3px; border: 0px; text-align: ${textAlign};">
                    ${escapeXml(cell)}
                </td>
            `;
        });
        html += '</tr>';
    });

    html += `
                                            </tbody>
                                        </table>
                                    </td>
                                    <td style="border: 0px; border-top: 1px solid #000; border-bottom: 1px solid #000; padding: 0px;">&nbsp;</td>
                                </tr>
                            </tbody>
                        </table>
                    </td>
                    <td style="border: 0px;">
                        <table border="0" cellpadding="0" cellspacing="0" style="border: 0;">
                            <tbody>
                                <tr>
                                    <td valign="top" style="border: 0px;">
                                        <sup>${escapeXml(matrixSuper)}</sup>
                                    </td>
                                </tr>
    `;

    for (let i = 0; i < parsedRows.length + 1; i += 1) {
        html += '<tr><td style="border: 0px;">&nbsp;</td></tr>';
    }

    html += `
                            </tbody>
                        </table>
                    </td>
                </tr>
            </tbody>
        </table><!-- end matrix -->
    `;

    if (matrixCenter) {
        html += '</center>';
    }

    return html;
}

const processZyMatrix = (node: JSONContent, docAttr: Record<string, any> | undefined): Record<string, any> => {
    const matrixSuper = node.attrs?.super || '';
    const matrixCenter = node.attrs?.center === true;
    const matrixLabel = node.attrs?.label || '';
    const matrixRows = normalizeMatrixRows(node.attrs?.matrix);
    const legacyHtml = node.attrs?.legacyHtml || '';

    // When the node was migrated from server HTML, legacyHtml holds the original nested table
    // structure. Use it directly to round-trip the server's exact markup rather than rebuilding.
    const html = legacyHtml ? legacyHtml : buildLegacyMatrixHtml(matrixLabel, matrixSuper, matrixCenter, matrixRows);

    return {
        ...CONTENT_RESOURCE_TEMPLATE,
        id: node.attrs?.id,
        type: 'html',
        payload: {
            html: [
                {
                    attributes: [{ type: 'html' }],
                    text: html,
                },
            ],
            image_ids: [],
            subject: docAttr?.attrs?.subjectName ?? DEFAULT_SECTION_EXPORT_SUBJECT,
            ref: node.attrs?.id,
        },
    };
};

const processProgressionBuilder = async (
    node: JSONContent,
    docAttr: Record<string, any> | undefined,
    ctx: ConvertContext,
): Promise<Record<string, any>> => {
    const { attrs } = node;
    const progressionId = attrs?.id;
    const toolName = attrs?.name || '';
    const caption = attrs?.caption || '';
    const altText = attrs?.alt_text || '';
    const instructions = attrs?.instructions || '';
    const challenge = attrs?.challenge || false;
    const parts = attrs?.parts || 1;
    const optional = attrs?.optional || false;
    const image_ids = attrs?.image_ids || [];
    const options = attrs?.options || {};

    let succeededImageIds: string[] = [];
    if (image_ids.length > 0) {
        const gDocIDs = await ctx.api.getDriveImagesAndMetadata(image_ids);
        succeededImageIds = Object.entries(gDocIDs).reduce<string[]>((acc, [key, value]) => {
            const v = value as { image?: object; error?: object };
            const validImage = v?.image && Object.keys(v.image).length > 0;
            const noError = !v.error || Object.keys(v.error).length === 0;

            if (validImage && noError) {
                acc.push(key);
            }
            return acc;
        }, []);
    }

    return {
        ...CONTENT_RESOURCE_TEMPLATE,
        activity_type: challenge ? 'challenge' : 'participation',
        type: 'custom',
        parts: parts,
        id: progressionId,
        caption: caption,
        instructions: instructions,
        payload: {
            tool: toolName,
            subject: docAttr?.attrs?.subjectName ?? DEFAULT_SECTION_EXPORT_SUBJECT,
            options: options,
            optional: optional,
            alt_text: altText,
            image_ids: succeededImageIds,
        },
    };
};

const processOthers = (node: JSONContent, docAttr: Record<string, any> | undefined): Record<string, any> => {
    const text = `
            <div style="border: 2px solid red; padding: 8px;">
              <p style="color: red; font-size: 18px; font-weight: bold; margin-bottom: 8px;">
                Unsupported content type: ${node.type}
              </p>
              <pre style="background: #f8d7da; color: #721c24; border-radius: 4px; padding: 12px; max-height: 200px; overflow: auto; font-size: 13px; line-height: 1.4;">
                ${JSON.stringify(node, null, 2)}
              </pre>
            </div>
          `;

    return {
        ...CONTENT_RESOURCE_TEMPLATE,
        type: 'html',
        payload: {
            html: [
                {
                    attributes: [{ type: 'html' }],
                    text,
                },
            ],
            image_ids: [],
            subject: docAttr?.attrs?.subjectName ?? DEFAULT_SECTION_EXPORT_SUBJECT,
            ref: '',
        },
    };
};

const processUnsupported = async (node: JSONContent, docAttr: Record<string, any> | undefined): Promise<Record<string, any>> => {
    try {
        return JSON.parse(node.attrs?.html);
    } catch (error) {
        console.log('Error parsing unsupported content JSON:', error);
    }

    const text = `
            <div style="border: 2px solid red; padding: 8px;">
              <p style="color: red; font-size: 18px; font-weight: bold; margin-bottom: 8px;">
                Malformed unsupported content
              </p>
              <pre style="background: #f8d7da; color: #721c24; border-radius: 4px; padding: 12px; max-height: 200px; overflow: auto; font-size: 13px; line-height: 1.4;">
                ${node.attrs?.html || 'No HTML content'}
              </pre>
            </div>
          `;

    return {
        ...CONTENT_RESOURCE_TEMPLATE,
        type: 'html',
        payload: {
            html: [
                {
                    attributes: [{ type: 'html' }],
                    text,
                },
            ],
            image_ids: [],
            subject: docAttr?.attrs?.subjectName ?? DEFAULT_SECTION_EXPORT_SUBJECT,
            ref: '',
        },
    };
};

const processUnsupportedEditable = async (node: JSONContent, docAttr: Record<string, any> | undefined): Promise<Record<string, any>> => {
    return {
        ...CONTENT_RESOURCE_TEMPLATE,
        id: node.attrs?.id,
        type: 'html',
        payload: {
            html: [
                {
                    attributes: [{ type: 'html' }],
                    text: `<div style="border: 2px solid #dc3545; padding: 12px; background: #f8d7da;">
  <p style="color: #721c24; font-weight: bold; margin: 0;">Unsupported content</p>
</div>`,
                },
            ],
            image_ids: [],
            subject: docAttr?.attrs?.subjectName ?? DEFAULT_SECTION_EXPORT_SUBJECT,
            ref: '',
        },
    };
};

const processZyAside = (node: JSONContent, _docAttr: Record<string, any> | undefined, ctx: ConvertContext): Record<string, any> => {
    void _docAttr; // docAttr unused; signature matches NodeProcessor
    const content = node.content?.reduce((acc, n) => acc + renderNodeHtml(n, ctx.deps), '') || '';

    // If the type is 'learning objective', set the type to 'bordered' for styling purposes
    const asideType = node.attrs?.type === 'learning objective' ? 'bordered' : node.attrs?.type || 'elaboration';
    return {
        ...CONTENT_RESOURCE_TEMPLATE,
        type: 'container',
        payload: {
            html: [
                {
                    attributes: [
                        {
                            type: 'html',
                        },
                    ],
                    text: content,
                },
            ],
            name: node.attrs?.caption,
            ref: node.attrs?.id,
            size: null,
            type: `aside-${asideType}`,
        },
    };
};

const processZyExample = async (
    node: JSONContent,
    docAttr: Record<string, any> | undefined,
    ctx: ConvertContext,
): Promise<Record<string, any>> => {
    const imageIds: string[] = [];
    let content = await processChildNodes(node.content || [], docAttr, ctx);

    // Collect image ids
    content.forEach(item => {
        if (item && item.attributes?.[0]?.image_ids) {
            imageIds.push(...(item.attributes[0].image_ids || []));
        }
    });

    let name = 'Example';
    if (node.attrs?.enumType) {
        name = `${toTitleCase(node.attrs.enumType)}`.trim();
    } else if (node.attrs?.type) {
        name = toTitleCase(node.attrs.type);
    }
    if (name === 'Figure') {
        content = content.map(item => {
            const attributes = item.attributes?.[0];
            if (attributes?.type !== 'image') {
                return item;
            }

            const normalizedAttributes = { ...attributes };
            if (normalizedAttributes.width != null) {
                normalizedAttributes.width = String(normalizedAttributes.width);
            } else {
                delete normalizedAttributes.width;
            }
            delete normalizedAttributes.height;
            delete normalizedAttributes.release_id;

            return {
                ...item,
                attributes: [normalizedAttributes],
            };
        });
        content = [{ text: '\n', attributes: [{ type: 'html' }] }, ...content, { text: '\n', attributes: [{ type: 'html' }] }];
    }

    return {
        ...CONTENT_RESOURCE_TEMPLATE,
        id: node.attrs?.id,
        type: 'container',
        caption: node.attrs?.caption || '',
        payload: {
            html: content,
            name,
            ref: node.attrs?.id || '',
            size: node.attrs?.size || null,
            image_ids: imageIds,
            subject: docAttr?.attrs?.subjectName ?? DEFAULT_SECTION_EXPORT_SUBJECT,
        },
    };
};

/**
 * zyBooks only builds its inline-enum lettering map from a single field per exercise
 * (question text enumids, else setup enumids, else solution enumids — see
 * `build_enum_id_map` in zybook_store.py). Enumids in a lower-priority field are
 * silently dropped there, and the reader still tries to look up any item tagged
 * `inline-enum` regardless of whether `enumid` is set, which throws. Downgrade those
 * items to plain html so the reader renders the formula without a broken lookup.
 */
function clearEnumIds(items: zybookHTMLContent[]): void {
    for (const item of items) {
        if (item.enumid) {
            item.enumid = '';
            item.attributes = [{ type: 'html' }];
        }
    }
}

const processZyExercise = async (
    node: JSONContent,
    docAttr: Record<string, any> | undefined,
    ctx: ConvertContext,
): Promise<Record<string, any>> => {
    const content = node.content || [];
    const setup: zybookHTMLContent[] = [];
    const questions: any[] = [];

    for (const childNode of content) {
        if (childNode.type === 'zySetup') {
            // Process setup content
            const setupContentNodes = childNode.content || [];
            const setupContent = await processChildNodes(setupContentNodes, docAttr, ctx);
            setup.push(...setupContent);
        } else if (childNode.type === 'zyProblem') {
            // Process problem
            const zyProblemContentNodes = childNode.content || [];
            const questionContentNodes: JSONContent[] = [];
            let solutionNodes: JSONContent[] = [];
            let studentView = false;

            // Separate question content and solution
            zyProblemContentNodes.forEach(item => {
                if (item.type === 'zyProblemSolution') {
                    solutionNodes = item.content || [];
                    studentView = item.attrs?.studentview ?? false;
                } else {
                    questionContentNodes.push(item);
                }
            });

            // Process question
            const questionContent = await processChildNodes(questionContentNodes, docAttr, ctx);

            // Process solution
            const solution = await processChildNodes(solutionNodes, docAttr, ctx);

            questions.push({
                student_view: studentView,
                text: questionContent,
                solution: solution.length > 0 ? solution : null,
            });
        }
    }

    const hasQuestionTextEnum = questions.some(q => q.text.some((item: zybookHTMLContent) => item.enumid));
    const hasSetupEnum = setup.some(item => item.enumid);
    if (hasQuestionTextEnum) {
        clearEnumIds(setup);
        questions.forEach(q => q.solution && clearEnumIds(q.solution));
    } else if (hasSetupEnum) {
        questions.forEach(q => q.solution && clearEnumIds(q.solution));
    }

    return {
        ...CONTENT_RESOURCE_TEMPLATE,
        type: 'exercise',
        parts: 0,
        caption: node.attrs?.caption || null,
        payload: {
            setup,
            questions,
            alt_text: '',
            image_ids: [],
            subject: docAttr?.attrs?.subjectName ?? DEFAULT_SECTION_EXPORT_SUBJECT,
            ref: node.attrs?.id || '',
            name: 'exercise',
        },
    };
};

const processZyDE = async (
    node: JSONContent,
    docAttr: Record<string, any> | undefined,
    ctx: ConvertContext,
): Promise<Record<string, any>> => {
    const attrs = node.attrs || {};
    const multifiles = attrs.multifiles ?? [];
    const mainFile = multifiles.find((f: { main?: boolean }) => f.main) || multifiles[0];
    const program = (mainFile as { program?: string } | undefined)?.program || '';

    const activityType = attrs.activityType;
    const isTinker = activityType === 'tinker';
    const activity_type = isTinker ? null : 'participation';
    const parts = isTinker ? 0 : 1;

    const inputRaw = attrs.inputText != null ? String(attrs.inputText) : '';
    const input = inputRaw.trim() !== '' ? inputRaw : null;

    const outRaw = attrs.outputFiles != null ? String(attrs.outputFiles) : '';
    const outputFiles = outRaw.trim() !== '' ? outRaw.trim() : null;

    const content = node.content || [];
    const instructionNode = content.find(n => n.type === 'zyDEInstruction');

    return {
        ...CONTENT_RESOURCE_TEMPLATE,
        id: attrs.id || '',
        type: 'custom',
        activity_type,
        parts,
        caption: attrs.caption || '',
        instructions: await processChildNodes(instructionNode?.content || [], docAttr, ctx),
        isModernAuthoringPreview: true,
        output_files_version: 2,
        payload: {
            tool: 'zyde',
            alt_text: '',
            locked: false,
            options: {
                language: attrs.language || 'cpp',
                stacked: Boolean(attrs.stacked),
                input_enabled: Boolean(attrs.inputEnabled),
                reset_enabled: Boolean(attrs.resetEnabled),
                input,
                outputFiles,
                program,
                multifiles,
            },
        },
    };
};

export async function buildColorizedZyCodeHtml(
    codeText: string,
    attrs: Record<string, unknown> = {},
    colorizeCodeFn: ZybooksApi['colorizeCode'],
): Promise<string> {
    const language = typeof attrs.language === 'string' ? attrs.language : 'text';
    const noboxClass = attrs.nobox ? ' no-box' : '';
    const highlight = typeof attrs.highlight === 'string' ? attrs.highlight : undefined;
    const numbering = Boolean(attrs.numbering);

    const colorize = colorizeCodeFn;
    const colorizedCode = await colorize(codeText, language, highlight, numbering);

    let htmlOutput = colorizedCode;
    if (colorizedCode === codeText) {
        htmlOutput = `<div class="code${noboxClass}"><pre>${escapeXml(codeText)}</pre></div>`;
    } else if (attrs.nobox) {
        htmlOutput = htmlOutput.replace(/<div class="(.*?)code(.*?)">/, `<div class="$1code${noboxClass}$2">`);
    }

    return htmlOutput;
}

const processZyCode = async (
    node: JSONContent,
    docAttr: Record<string, any> | undefined,
    ctx: ConvertContext,
): Promise<Record<string, any>> => {
    const attrs = node.attrs || {};
    const content = node.content || [];

    const codeText = content.map(n => n.text || '').join('');
    const htmlOutput = await buildColorizedZyCodeHtml(codeText, attrs, ctx.api.colorizeCode);

    return {
        ...CONTENT_RESOURCE_TEMPLATE,
        id: attrs.id,
        type: 'html',
        payload: {
            html: [
                {
                    text: htmlOutput,
                    attributes: [{ type: 'html' }],
                },
            ],
            image_ids: [],
            subject: docAttr?.attrs?.subjectName ?? DEFAULT_SECTION_EXPORT_SUBJECT,
            ref: attrs.id || '',
        },
    };
};

const processZyCommonError = (node: JSONContent, docAttr: Record<string, any> | undefined, ctx: ConvertContext): Record<string, any> => {
    const attrs = node.attrs || {};
    const content = node.content || [];

    // Extract only text content
    let htmlContent = content.map(n => renderNodeHtml(n, ctx.deps)).join('');

    // Replace "common error" with the styled version
    htmlContent = htmlContent.replace(/(common\s+error)/i, '<span class="common-error-term">$1</span>');

    return {
        ...CONTENT_RESOURCE_TEMPLATE,
        type: 'html',
        payload: {
            html: [
                {
                    text: `<span class="common-error">${htmlContent}</span>`,
                    attributes: [{ type: 'html' }],
                },
            ],
            image_ids: [],
            subject: docAttr?.attrs?.subjectName ?? DEFAULT_SECTION_EXPORT_SUBJECT,
            ref: attrs.id || '',
        },
    };
};

const processZyConsole = (node: JSONContent, docAttr: Record<string, any> | undefined): Record<string, any> => {
    const attrs = node.attrs || {};
    const content = node.content || [];
    const preserveWhiteSpace = attrs['preserve-white-space'] || false;
    const highlight = attrs.highlight || '';

    const textContent = content.map(n => n.text || '').join('');

    const highlightedLines: number[] = highlight
        ? highlight
              .split(',')
              .map((n: string) => parseInt(n.trim()))
              .filter((n: number) => !isNaN(n))
        : [];

    const processedContent = textContent
        .split('\n')
        .map((line, index) => {
            const escaped = escapeXml(line);
            return highlightedLines.includes(index + 1) ? `<span class="codehighlight">${escaped}</span>` : escaped;
        })
        .join('\n');

    const preStyle = preserveWhiteSpace ? ' style="white-space: pre;"' : '';
    const htmlOutput = `<div class="console"><pre${preStyle}>${processedContent}</pre></div>`;

    return {
        ...CONTENT_RESOURCE_TEMPLATE,
        type: 'html',
        payload: {
            html: [
                {
                    text: htmlOutput,
                    attributes: [{ type: 'html' }],
                },
            ],
            image_ids: [],
            subject: docAttr?.attrs?.subjectName ?? DEFAULT_SECTION_EXPORT_SUBJECT,
            ref: attrs.id || '',
        },
    };
};

const processZyExplore = (node: JSONContent, docAttr: Record<string, any> | undefined, ctx: ConvertContext): Record<string, any> => {
    const content = node.content?.reduce((acc, n) => acc + renderNodeHtml(n, ctx.deps), '') || '';
    return {
        ...CONTENT_RESOURCE_TEMPLATE,
        type: 'html',
        payload: {
            html: [
                {
                    attributes: [
                        {
                            type: 'html',
                        },
                    ],
                    text: `<div class="explore">Exploring further: ${content}</div><div style="clear: both;"></div>`,
                },
            ],
            image_ids: [],
            subject: docAttr?.attrs?.subjectName ?? DEFAULT_SECTION_EXPORT_SUBJECT,
            ref: node.attrs?.id || '',
        },
    };
};

const processZyInlineEnum = (node: JSONContent, docAttr: Record<string, any> | undefined, ctx: ConvertContext): Record<string, any> => {
    let text = '';
    if (node.content && node.content[0]) {
        const isMathMl = Boolean(node.content[0].attrs?.mathml);
        text = renderNodeHtml(node.content[0], ctx.deps);
        text = isMathMl ? text : escapeXml(text);
    }
    return {
        ...CONTENT_RESOURCE_TEMPLATE,
        type: 'html',
        payload: {
            html: [
                {
                    text,
                    ref: '',
                    enumid: node.attrs?.id || '',
                    attributes: [
                        {
                            type: 'inline-enum',
                        },
                    ],
                },
            ],
            image_ids: [],
            subject: docAttr?.attrs?.subjectName ?? DEFAULT_SECTION_EXPORT_SUBJECT,
            ref: node.attrs?.id || '',
        },
    };
};

const processZyStudio = (node: JSONContent, docAttr: Record<string, any> | undefined, ctx: ConvertContext): Record<string, any> => {
    const attrs = node.attrs || {};
    const content = node.content || [];

    // Extract instructions from zyStudioInstruction content
    let instructions = '';
    const instructionNode = content.find(n => n.type === 'zyStudioInstruction');
    if (instructionNode && instructionNode.content) {
        instructions = instructionNode.content.map(n => renderNodeHtml(n, ctx.deps)).join('');
    }

    return {
        ...CONTENT_RESOURCE_TEMPLATE,
        id: attrs.id,
        type: 'zystudio',
        activity_type: attrs.activityType === 'lab' ? 'lab' : null,
        parts: 1,
        caption: attrs.caption || '',
        instructions: instructions || null,
        payload: {},
        release_id: docAttr?.releaseId || '',
        guid: attrs.guid || '',
    };
};

const processZyITLab = (node: JSONContent, docAttr: Record<string, any> | undefined, ctx: ConvertContext): Record<string, any> => {
    const { files, items, vmImages, modelSolution, parts, instructions } = buildZyITLabPayload(node, ctx.deps.getExtensions());
    const initialParts = Array.from({ length: parts }, () => ({ complete: 0 }));

    return {
        ...CONTENT_RESOURCE_TEMPLATE,
        id: node.attrs?.id,
        type: 'itlab',
        activity_type: 'lab',
        parts,
        caption: node.attrs?.caption || '',
        instructions,
        release_id: docAttr?.releaseId || '',
        activityData: {
            parts: initialParts,
            current_vms_streamed_parts: initialParts.map(() => ({ complete: 0 })),
        },
        payload: {
            files,
            items,
            vmImages,
            modelSolution,
        },
    };
};

const processZyClassicLab = async (
    node: JSONContent,
    docAttr: Record<string, any> | undefined,
    ctx: ConvertContext,
): Promise<Record<string, any>> => {
    const attrs = node.attrs || {};
    const content = node.content || [];
    const instructionNode = content.find(n => n.type === 'zyClassicLabInstruction');

    const html = await processChildNodes(instructionNode?.content || [], docAttr, ctx);
    const notSupportedHTMLMessage = `
        <div style="margin: 1.5rem 0; padding: 1rem 1.25rem; background-color: #fef2f2; border: 1px solid #fecaca; border-left: 4px solid #dc2626; border-radius: 6px;">
            <p style="margin: 0 0 0.35rem; color: #991b1b; font-weight: 600; font-size: 0.95rem;">
                Classic Lab preview is not supported
            </p>
            <p style="margin: 0; color: #b91c1c; font-size: 0.875rem; line-height: 1.45;">
                Instruction content appears above. Open the lab in zyBooks to view.
            </p>
        </div>
    `;
    html.push({
        text: notSupportedHTMLMessage,
        attributes: [{ type: 'html' }],
    });

    return {
        ...CONTENT_RESOURCE_TEMPLATE,
        id: attrs.id,
        type: 'html',
        parts: 0,
        payload: { html },
        release_id: docAttr?.releaseId || '',
    };
};

const processZyFootnote = (contentResource: Record<string, any>, docAttr?: Record<string, any>): Record<string, any>[] => {
    const footnoteRegex = /<zyFootnote id=\\?"([^"\\]+)\\?" name=\\?"([^"\\]*)\\?"([^>]*)>([\s\S]*?)<\/zyFootnote>/g;
    const footnoteContentResources: Record<string, any>[] = [];

    // Convert the content resource to a string
    let contentResourceString = JSON.stringify(contentResource);

    // Replace zyFootnote tags with anchors and create footnote content resources
    contentResourceString = contentResourceString.replace(
        footnoteRegex,
        (_match: string, id: string, number: string, _attrs: string, innerContent: string) => {
            // innerContent is a JSON-string fragment (e.g. href=\"…\"); unescape before
            // embedding in a real JS string. Leaving \" in place makes hrefs relative
            // (…/%22https:/…/%22) in preview.
            const unescapedInner = JSON.parse(`"${innerContent}"`);
            footnoteContentResources.push({
                ...CONTENT_RESOURCE_TEMPLATE,
                id,
                type: 'html',
                order: parseInt(number, 10),
                payload: {
                    html: [
                        {
                            attributes: [{ type: 'html' }],
                            text: `<p id="${id}-reference" class="footnote-reference">(*${number}) ${unescapedInner}</p>`,
                        },
                    ],
                    image_ids: [],
                    subject: docAttr?.attrs?.subjectName ?? DEFAULT_SECTION_EXPORT_SUBJECT,
                    ref: id,
                },
            });
            return `<sup id=\\"${id}-anchor\\" class=\\"footnote-anchor\\">${number}</sup>`;
        },
    );

    return [JSON.parse(contentResourceString), ...footnoteContentResources];
};

const extractEnumIds = (resource: Record<string, any>): string[] => {
    const enumIds: string[] = [];
    const payload = resource.payload;

    if (!payload) {
        return enumIds;
    }

    if (payload.html && Array.isArray(payload.html)) {
        payload.html.forEach((item: any) => {
            if (item.enumid) {
                enumIds.push(item.enumid);
            }
        });
    }

    // Special handling for enumids in exercises
    if (resource.type === 'exercise') {
        if (payload.questions && Array.isArray(payload.questions)) {
            payload.questions.forEach((question: any) => {
                if (question.text && Array.isArray(question.text)) {
                    question.text.forEach((item: any) => {
                        if (item.enumid) {
                            enumIds.push(item.enumid);
                        }
                    });
                }
                if (question.solution && Array.isArray(question.solution)) {
                    question.solution.forEach((item: any) => {
                        if (item.enumid) {
                            enumIds.push(item.enumid);
                        }
                    });
                }
            });
        }

        if (payload.setup && Array.isArray(payload.setup)) {
            payload.setup.forEach((setupItem: any) => {
                if (setupItem.enumid) {
                    enumIds.push(setupItem.enumid);
                }
            });
        }
    }

    return enumIds;
};

const buildEnumIdsMap = (
    enumIds: string[],
    chapterNumber: number,
    sectionNumber: number,
    currentTree: EnumTree,
    currentMap: InlineEnumMap,
): { tree: EnumTree; map: InlineEnumMap } => {
    if (!chapterNumber || !sectionNumber || enumIds.length === 0) {
        return { tree: currentTree, map: currentMap };
    }

    const tree = { ...currentTree };
    const map = { ...currentMap };

    if (!tree[chapterNumber]) {
        tree[chapterNumber] = {};
    }
    if (!tree[chapterNumber][sectionNumber]) {
        tree[chapterNumber][sectionNumber] = {};
    }

    const sectionEnums = { ...tree[chapterNumber][sectionNumber] };

    enumIds.forEach(enumId => {
        const currentIndex = Object.keys(sectionEnums).length;
        const letter = alphabetEnumerate(currentIndex);

        sectionEnums[enumId] = letter;
        map[enumId] = {
            chapter_number: chapterNumber,
            letter,
            section_number: sectionNumber,
        };
    });

    tree[chapterNumber][sectionNumber] = sectionEnums;

    return { tree, map };
};

const processBlockOrderingBuilder = (node: JSONContent, docAttr: Record<string, any> | undefined): Record<string, any> => {
    const { attrs } = node;
    const blockOrderingId = attrs?.id;
    const toolName = attrs?.name || '';
    const caption = attrs?.caption || '';
    const challenge = attrs?.challenge || false;
    const options = attrs?.options || {};

    return {
        ...CONTENT_RESOURCE_TEMPLATE,
        activity_type: challenge ? 'challenge' : 'participation',
        type: 'custom',
        parts: 1,
        id: blockOrderingId,
        caption: caption,
        payload: {
            tool: toolName,
            subject: docAttr?.attrs?.subjectName ?? DEFAULT_SECTION_EXPORT_SUBJECT,
            options: options,
            alt_text: '',
            image_ids: [],
        },
    };
};

const processStreamlitApp = (node: JSONContent, docAttr: Record<string, any> | undefined): Record<string, any> => {
    const { attrs } = node;
    const streamlitId = attrs?.id;
    const caption = attrs?.caption || '';
    const options = attrs?.options || {};

    return {
        ...CONTENT_RESOURCE_TEMPLATE,
        activity_type: null,
        type: 'custom',
        parts: 0,
        id: streamlitId,
        caption,
        payload: {
            tool: 'StreamlitApp',
            subject: docAttr?.attrs?.subjectName ?? DEFAULT_SECTION_EXPORT_SUBJECT,
            options: {
                code: options.code ?? '',
                guid: options.guid ?? streamlitId,
                streamlit_app_id: options.streamlit_app_id,
            },
            alt_text: '',
            image_ids: [],
        },
    };
};

const processCodeWritingBuilder = (node: JSONContent, docAttr: Record<string, any> | undefined): Record<string, any> => {
    const { attrs } = node;
    const codeWritingId = attrs?.id;
    const caption = attrs?.caption || '';
    const challenge = attrs?.challenge || false;
    const options = attrs?.options || {};
    const parts = attrs?.parts || 1;

    return {
        ...CONTENT_RESOURCE_TEMPLATE,
        activity_type: challenge ? 'challenge' : 'participation',
        type: 'custom',
        parts: parts,
        id: codeWritingId,
        caption: caption,
        payload: {
            tool: 'codeWriting',
            subject: docAttr?.attrs?.subjectName ?? DEFAULT_SECTION_EXPORT_SUBJECT,
            options: options,
            alt_text: '',
            image_ids: [],
        },
    };
};

const processCodeOutputBuilder = (node: JSONContent, docAttr: Record<string, any> | undefined): Record<string, any> => {
    const { attrs } = node;
    const codeOutputId = attrs?.id;
    const caption = attrs?.caption || '';
    const options = attrs?.options || {};
    const parts = attrs?.parts || 1;

    return {
        ...CONTENT_RESOURCE_TEMPLATE,
        activity_type: 'challenge',
        type: 'custom',
        parts: parts,
        id: codeOutputId,
        caption: caption,
        payload: {
            tool: 'codeOutput',
            subject: docAttr?.attrs?.subjectName ?? DEFAULT_SECTION_EXPORT_SUBJECT,
            options: options,
            alt_text: '',
            image_ids: [],
        },
    };
};

const processParsonsCodingBuilder = (node: JSONContent, docAttr: Record<string, any> | undefined): Record<string, any> => {
    const { attrs } = node;
    const parsonsId = attrs?.id;
    const caption = attrs?.caption || '';
    const challenge = attrs?.challenge || false;
    const options = attrs?.options || {};

    return {
        ...CONTENT_RESOURCE_TEMPLATE,
        activity_type: challenge ? 'challenge' : 'participation',
        type: 'custom',
        parts: 1,
        id: parsonsId,
        caption: caption,
        payload: {
            tool: 'parsonsCodingPA',
            subject: docAttr?.attrs?.subjectName ?? DEFAULT_SECTION_EXPORT_SUBJECT,
            options: options,
            alt_text: '',
            image_ids: [],
        },
    };
};

const removeParagraphNodes = (node: JSONContent): JSONContent[] => {
    const flatten = (nodes: JSONContent[]): JSONContent[] => {
        const result: JSONContent[] = [];
        for (const child of nodes) {
            if (child.type === 'paragraph') {
                result.push(...flatten(child.content || []));
            } else if (child.content?.length) {
                result.push({
                    ...child,
                    content: flatten(child.content),
                });
            } else {
                result.push(child);
            }
        }
        return result;
    };
    return flatten(node.content || []);
};

const getLayoutGridColumnCount = (node: JSONContent): number => {
    const firstRow = node.content?.[0];
    if (!firstRow?.content?.length) {
        return 1;
    }

    return firstRow.content.reduce((count, cell) => {
        const colspan = Number(cell.attrs?.colspan) || 1;
        return count + colspan;
    }, 0);
};

const processLayoutGridCellContent = async (
    cell: JSONContent,
    docAttr: Record<string, any> | undefined,
    ctx: ConvertContext,
): Promise<{ cellHtml: string; index_items: IndexItem[] }> => {
    const processed = await processHTMLWithCustomNode(cell, docAttr, ctx);
    const processedHtml = processed?.payload?.html;
    let cellHtml = '';
    if (Array.isArray(processedHtml)) {
        for (const item of processedHtml) {
            const attr = item.attributes?.[0];
            if (item.text != null) {
                cellHtml += item.text;
            } else if (attr?.type === 'image') {
                cellHtml += generateImgTag(item, {
                    subject: attr.subject ?? docAttr?.attrs?.subjectName ?? DEFAULT_SECTION_EXPORT_SUBJECT,
                    releaseId: attr.release_id ?? String(docAttr?.attrs?.releaseId ?? DEFAULT_SECTION_EXPORT_RELEASE_ID),
                });
            }
        }
    } else if (typeof processedHtml === 'string') {
        cellHtml = processedHtml;
    } else {
        throw new Error('Invalid HTML items');
    }

    return { cellHtml, index_items: processed.index_items || [] };
};

const processLayoutGrid = async (
    node: JSONContent,
    docAttr: Record<string, any> | undefined,
    ctx: ConvertContext,
): Promise<Record<string, any>> => {
    const columnCount = getLayoutGridColumnCount(node);
    const wrapperAttrs: string[] = [`class="grid-layout"`, `style="--grid-layout-columns: ${columnCount};"`];
    if (node.attrs?.id != null) {
        wrapperAttrs.push(`data-id="${node.attrs.id}"`);
    }

    const cellHtmlParts: string[] = [];
    let index_items: IndexItem[] = [];

    for (const row of node.content || []) {
        for (const cell of row.content || []) {
            const { cellHtml, index_items: cellIndexItems } = await processLayoutGridCellContent(cell, docAttr, ctx);
            if (cellIndexItems.length) {
                index_items = index_items.concat(cellIndexItems);
            }

            const cellAttrs: string[] = ['class="grid-layout-cell"'];
            if (cell.attrs?.id) {
                cellAttrs.push(`data-id="${cell.attrs.id}"`);
            }

            const cellStyles: string[] = [];
            const colspan = Number(cell.attrs?.colspan) || 1;
            if (colspan > 1) {
                cellStyles.push(`--grid-layout-colspan: ${colspan};`);
            }
            const rowspan = Number(cell.attrs?.rowspan) || 1;
            if (rowspan > 1) {
                cellStyles.push(`--grid-layout-rowspan: ${rowspan};`);
            }
            if (cell.attrs?.nodeTextAlign) {
                cellStyles.push(`justify-self: ${cell.attrs.nodeTextAlign};`);
            }
            if (cell.attrs?.nodeVerticalAlign) {
                let alignSelf = '';
                switch (cell.attrs.nodeVerticalAlign) {
                    case 'top':
                        alignSelf = 'start';
                        break;
                    case 'middle':
                        alignSelf = 'center';
                        break;
                    case 'bottom':
                        alignSelf = 'end';
                        break;
                }
                cellStyles.push(`align-self:${alignSelf};`);
            }
            if (cellStyles.length > 0) {
                cellAttrs.push(`style="${cellStyles.join(' ')}"`);
            }

            cellHtmlParts.push(`<div ${cellAttrs.join(' ')}>${cellHtml}</div>`);
        }
    }

    const finalHtml = `<div ${wrapperAttrs.join(' ')}>${cellHtmlParts.join('')}</div>`;
    return {
        ...CONTENT_RESOURCE_TEMPLATE,
        id: node.attrs?.id,
        type: 'html',
        index_items,
        payload: {
            html: [
                {
                    attributes: [{ type: 'html' }],
                    text: finalHtml,
                },
            ],
            image_ids: [],
            subject: docAttr?.attrs?.subjectName ?? DEFAULT_SECTION_EXPORT_SUBJECT,
            ref: node.attrs?.id,
        },
    };
};

const processZyTable = async (
    node: JSONContent,
    docAttr: Record<string, any> | undefined,
    ctx: ConvertContext,
): Promise<Record<string, any>> => {
    if (node.attrs?.isLayoutGrid) {
        return processLayoutGrid(node, docAttr, ctx);
    }

    const tag = node.type;
    const processedTable = await processHTMLWithCustomNode(node, docAttr, ctx);

    const htmlItems = processedTable?.payload?.html;
    let tableHtml = '';
    if (Array.isArray(htmlItems)) {
        for (const item of htmlItems) {
            if (item.text === null) {
                continue;
            } else {
                tableHtml += item.text;
            }
        }
    } else if (typeof htmlItems === 'string') {
        tableHtml = htmlItems;
    } else {
        throw new Error('Invalid HTML items');
    }

    const openTagAttrs: string[] = [];
    if (node.attrs?.id != null) {
        openTagAttrs.push(`data-id="${node.attrs.id}"`);
    }
    if (node.attrs?.isDatabaseTable) {
        openTagAttrs.push(`class="dbTable"`);
    }

    const tableStyles: string[] = [];
    if (node.attrs?.nodeTextAlign) {
        tableStyles.push(`text-align: ${node.attrs.nodeTextAlign}`);
    }
    if (tableStyles.length > 0) {
        openTagAttrs.push(`style="${tableStyles.join(';')};"`);
    }

    const tableAttrs = openTagAttrs.length > 0 ? ` ${openTagAttrs.join(' ')}` : '';
    const colgroup = renderTableColgroupHtml(node, ctx.deps.getExtensions());
    const finalTableHtml = `<div class="table"><${tag}${tableAttrs}>${colgroup}${tableHtml}</${tag}></div>`;
    return {
        ...CONTENT_RESOURCE_TEMPLATE,
        id: node.attrs?.id,
        type: 'html',
        index_items: processedTable.index_items,
        payload: {
            html: [
                {
                    attributes: [{ type: 'html' }],
                    text: finalTableHtml,
                },
            ],
            image_ids: [],
            subject: docAttr?.attrs?.subjectName ?? DEFAULT_SECTION_EXPORT_SUBJECT,
            ref: node.attrs?.id,
        },
    };
};

const processTableRow = async (
    node: JSONContent,
    docAttr: Record<string, any> | undefined,
    ctx: ConvertContext,
): Promise<Record<string, any>> => {
    const processed = await processHTMLWithCustomNode(node, docAttr, ctx);
    const processedHtml = processed?.payload?.html;
    let rowHtml = '';

    if (Array.isArray(processedHtml)) {
        for (const item of processedHtml) {
            if (item.text === null) {
                continue;
            } else {
                rowHtml += item.text;
            }
        }
    } else if (typeof processedHtml === 'string') {
        rowHtml = processedHtml;
    } else {
        throw new Error('Invalid HTML items');
    }

    return {
        ...CONTENT_RESOURCE_TEMPLATE,
        id: node.attrs?.id,
        type: 'html',
        index_items: processed.index_items,
        payload: {
            html: [
                {
                    attributes: [{ type: 'html' }],
                    text: `<tr data-id="${node.attrs?.id}">${rowHtml}</tr>`,
                },
            ],
            image_ids: [],
            subject: docAttr?.attrs?.subjectName ?? DEFAULT_SECTION_EXPORT_SUBJECT,
            ref: node.attrs?.id,
        },
    };
};

const processTableCell = async (
    node: JSONContent,
    docAttr: Record<string, any> | undefined,
    ctx: ConvertContext,
): Promise<Record<string, any>> => {
    const tag = node.type === 'tableHeader' ? 'th' : 'td';
    // Remove paragraph nodes as they cause spacing issues
    node.content = removeParagraphNodes(node);
    const processed = await processHTMLWithCustomNode(node, docAttr, ctx);
    const processedHtml = processed?.payload?.html;
    let cellHtml = '';
    if (Array.isArray(processedHtml)) {
        for (const item of processedHtml) {
            const attr = item.attributes?.[0];
            if (item.text != null) {
                cellHtml += item.text;
            } else if (attr?.type === 'image') {
                cellHtml += generateImgTag(item, {
                    subject: attr.subject ?? docAttr?.attrs?.subjectName ?? DEFAULT_SECTION_EXPORT_SUBJECT,
                    releaseId: attr.release_id ?? String(docAttr?.attrs?.releaseId ?? DEFAULT_SECTION_EXPORT_RELEASE_ID),
                });
            }
        }
    } else if (typeof processedHtml === 'string') {
        cellHtml = processedHtml;
    } else {
        throw new Error('Invalid HTML items');
    }

    // Add non-style attributes
    const tableCellAttrs: string[] = [];
    if (node.attrs?.id) {
        tableCellAttrs.push(`data-id="${node.attrs.id}"`);
    }
    if (node.attrs?.colspan) {
        tableCellAttrs.push(`colspan=${node.attrs.colspan}`);
    }
    if (node.attrs?.colwidth) {
        tableCellAttrs.push(`colwidth=${[node.attrs.colwidth]}`);
    }
    if (node.attrs?.rowspan) {
        tableCellAttrs.push(`rowspan=${node.attrs.rowspan}`);
    }
    const importedWidth = node.attrs?.importedWidth;
    if (importedWidth && importedWidth > 0) {
        tableCellAttrs.push(`width="${Math.round(importedWidth)}%"`);
    }

    // Header Key attributes
    const headerKeyClasses: string[] = [];
    let isKey = false;
    if (node.attrs?.primaryKey) {
        headerKeyClasses.push(`dbPrimaryKey`);
        isKey = true;
    }
    if (node.attrs?.foreignKey) {
        headerKeyClasses.push(`dbForeignKey`);
        isKey = true;
    }
    if (headerKeyClasses.length > 0) {
        tableCellAttrs.push(`class="${headerKeyClasses.join(' ')}"`);
    }

    // Style attributes
    const tableCellStyles: string[] = [];
    if (node.attrs?.nodeTextAlign) {
        tableCellStyles.push(`text-align: ${node.attrs.nodeTextAlign};`);
    }
    if (node.attrs?.align) {
        tableCellStyles.push(`align: ${node.attrs.align};`);
    }
    if (node.attrs?.nodeVerticalAlign) {
        tableCellStyles.push(`vertical-align:${node.attrs.nodeVerticalAlign};`);
    }
    if (tableCellStyles.length > 0) {
        tableCellAttrs.push(`style="${tableCellStyles.join(' ')}"`);
    }

    return {
        ...CONTENT_RESOURCE_TEMPLATE,
        id: node.attrs?.id,
        type: 'html',
        index_items: processed.index_items,
        payload: {
            html: [
                {
                    attributes: [{ type: 'html' }],
                    text: `<${tag} ${tableCellAttrs.join(' ')}>${isKey ? ` ${cellHtml}` : cellHtml}</${tag}>`,
                },
            ],
            image_ids: [],
            subject: docAttr?.attrs?.subjectName ?? DEFAULT_SECTION_EXPORT_SUBJECT,
            ref: node.attrs?.id,
        },
    };
};

const processList = async (
    node: JSONContent,
    docAttr: Record<string, any> | undefined,
    ctx: ConvertContext,
): Promise<Record<string, any>> => {
    const tag = node.type === 'orderedList' ? 'ol' : 'ul';
    const listItems = node.content || [];
    const listItemHtmls: string[] = [];
    let index_items: IndexItem[] = [];

    for (const listItem of listItems) {
        const processed = await processHTMLWithCustomNode(listItem, docAttr, ctx);
        const htmlItems = processed?.payload?.html;
        if (Array.isArray(htmlItems)) {
            listItemHtmls.push(htmlItems.map(item => (item.text != null ? item.text : '')).join(''));
        } else if (typeof htmlItems === 'string') {
            listItemHtmls.push(htmlItems);
        } else {
            throw new Error('Invalid HTML items');
        }
        if (processed?.index_items?.length) {
            index_items = index_items.concat(processed.index_items);
        }
    }

    const openTagAttrs: string[] = [];
    if (node.attrs?.id != null) {
        openTagAttrs.push(`id="${node.attrs.id}"`);
    }
    if (tag === 'ol' && node.attrs?.type != null) {
        openTagAttrs.push(`type="${node.attrs.type}"`);
    }
    const openTag = openTagAttrs.length > 0 ? `<${tag} ${openTagAttrs.join(' ')}>` : `<${tag}>`;
    const listHtml = stripListItemParagraph(`${openTag}${listItemHtmls.map(inner => `<li>${inner}</li>`).join('')}</${tag}>`);

    return {
        ...CONTENT_RESOURCE_TEMPLATE,
        id: node.attrs?.id,
        type: 'html',
        index_items,
        payload: {
            html: [
                {
                    attributes: [{ type: 'html' }],
                    text: listHtml,
                },
            ],
            image_ids: [],
            subject: docAttr?.attrs?.subjectName ?? DEFAULT_SECTION_EXPORT_SUBJECT,
            ref: node.attrs?.id,
        },
    };
};

/**
 * Adds a period to the end of a caption (if there isn't already a punctuation mark there),
 * and processes any ZYFUNCTION or ZYFCT keywords.
 * @param zybooksJson - The content resource to process
 * @returns The content resource with the caption processed
 */
const processCaption = (zybooksJson: Record<string, any>): Record<string, any> => {
    zybooksJson.caption = zybooksJson.caption?.trim() || '';

    if (!zybooksJson.caption) {
        return zybooksJson;
    }

    // TODO: Process ZYFUNCTION or ZYFCT keywords once we have zyVersions working.

    if (['.', '?', '!'].includes(zybooksJson.caption.at(-1))) {
        return zybooksJson;
    }

    zybooksJson.caption = `${zybooksJson.caption}.`;

    return zybooksJson;
};

const processOpenStaxPythonCodeRunnerBuilder = (node: JSONContent, docAttr: Record<string, any> | undefined): Record<string, any> => {
    const { attrs } = node;
    const openStaxPythonCodeRunnerBuilderId = attrs?.id;
    const caption = attrs?.caption || '';
    const options = attrs?.options || {};
    const instructions = attrs?.instructions || '';

    return {
        ...CONTENT_RESOURCE_TEMPLATE,
        activity_type: 'participation',
        type: 'custom',
        parts: 0,
        id: openStaxPythonCodeRunnerBuilderId,
        caption: caption,
        instructions: instructions,
        payload: {
            tool: 'OpenStaxPythonCodeRunnerWrapper',
            subject: docAttr?.attrs?.subjectName ?? DEFAULT_SECTION_EXPORT_SUBJECT,
            options: options,
            alt_text: '',
            image_ids: [],
        },
    };
};

/**
 * Flattens an array of JSONContent nodes that may contain nested arrays or nulls,
 * producing a flat array of JSONContent nodes (removing nulls).
 *
 * @param results - Array of JSONContent, JSONContent arrays, or null values to flatten
 * @returns A flat array of JSONContent nodes
 */
function flattenTransformed(results: (JSONContent | JSONContent[] | null)[]): JSONContent[] {
    const out: JSONContent[] = [];
    for (const r of results) {
        if (r === null) continue;
        if (Array.isArray(r)) out.push(...r);
        else out.push(r);
    }
    return out;
}

function intersectVersionSets(a: Set<string>, b: Set<string>): Set<string> {
    const out = new Set<string>();
    for (const token of a) {
        if (b.has(token)) {
            out.add(token);
        }
    }
    return out;
}

/**
 * Recursively transforms nodes of specified type
 * Preserves structure for other nodes.
 *
 * @param node - The JSONContent node to process
 * @param selectedZyVersion - The document filter (legacy string, multi-select array, or null)
 * @returns The transformed node or array of nodes, or null if filtered out
 */
function transformNode(
    node: JSONContent,
    selectedZyVersion: SelectedZyVersionFilter,
    deps: ConvertProseMirrorToZybooksJSONDeps,
    inheritedVersionSet: Set<string> | null = null,
): JSONContent | JSONContent[] | null {
    const type = node.type as string;
    if (type === 'zyVersionBlock' || type === 'zyVersionInline') {
        const nodeVersion = normalizeVersion(node.attrs?.version);
        const nodeVersionSet = new Set(nodeVersion);

        if (!isZyVersionNodeVisible(selectedZyVersion, node.attrs?.version ?? '')) {
            return null;
        }

        if (inheritedVersionSet && nodeVersionSet.size > 0 && intersectVersionSets(inheritedVersionSet, nodeVersionSet).size === 0) {
            return null;
        }

        const nextInheritedVersionSet = (() => {
            if (nodeVersionSet.size === 0) {
                return inheritedVersionSet;
            }
            if (!inheritedVersionSet) {
                return nodeVersionSet;
            }
            return intersectVersionSets(inheritedVersionSet, nodeVersionSet);
        })();

        return flattenTransformed((node.content ?? []).map(c => transformNode(c, selectedZyVersion, deps, nextInheritedVersionSet)));
    }
    const content = node.content;
    return {
        ...node,
        content: content ? flattenTransformed(content.map(c => transformNode(c, selectedZyVersion, deps, inheritedVersionSet))) : undefined,
    };
}

/**
 * Returns a new document JSON by recursively transforming the tree: for nodes that
 * match a condition, unwrap (replace with their content); for those that do not, remove.
 * Does not mutate the input. Currently used to resolve zyVersion nodes by
 * the provided version filter.
 */
export function transformDoc(
    doc: JSONContent,
    selectedZyVersion: SelectedZyVersionFilter = null,
    deps: ConvertProseMirrorToZybooksJSONDeps,
): JSONContent {
    const content = doc.content ?? [];
    return {
        ...doc,
        content: flattenTransformed(content.map(c => transformNode(c, selectedZyVersion, deps))),
    };
}

/**
 * Processes a list of nodes into a flat array of content items
 * (payload.html). Needed when child content can include custom nodes like zyInlineEnum: those must
 * produce the right content items (e.g. attributes.type 'inline-enum') instead of
 * being rendered as plain HTML.
 *
 * @param nodes - Child JSONContent nodes to process
 * @returns Flattened array of content items from each node's payload.html
 */
const processChildNodes = async (
    nodes: JSONContent[],
    docAttr: Record<string, any> | undefined,
    ctx: ConvertContext,
): Promise<Array<zybookHTMLContent>> => {
    const content = [];
    const processedChildNodes = await Promise.all(
        nodes.map(node => processNode(node, docAttr, { skipGuidGeneration: true, processingChildNodes: true }, ctx)),
    );
    for (const processedNode of processedChildNodes) {
        if (processedNode?.payload?.html && Array.isArray(processedNode.payload.html)) {
            content.push(...processedNode.payload.html);
        }
    }
    return content;
};

const processNode = async (
    node: JSONContent,
    docAttr: Record<string, any> | undefined,
    options: ProcessNodeOptions | undefined,
    ctx: ConvertContext,
): Promise<Record<string, any> | null> => {
    if (!options?.skipGuidGeneration && !docAttr?.sectionId) {
        throw new Error('Missing sectionId for content resource GUID generation.');
    }
    const processors: Record<string, NodeProcessor> = {
        heading: processHTML,
        paragraph: processHTML,
        blockMath: processHTML,
        zydefnBlock: processHTML,
        codeBlock: processHTML,
        blockquote: processHTML,
        horizontalRule: processHTML,
        iframe: processIframe,
        details: processHTML,
        listItem: processHTMLWithCustomNode,
        bulletList: processList,
        orderedList: processList,
        image: options?.processingChildNodes ? processChildImage : processImageNode,
        zyAside: processZyAside,
        zyExample: processZyExample,
        zyExplore: processZyExplore,
        zyInlineEnum: processZyInlineEnum,
        reference: processReference,
        zyStudio: processZyStudio,
        zyITLab: processZyITLab,
        zyClassicLab: processZyClassicLab,
        questionSet: processQuestionSet,
        zyExercise: processZyExercise,
        zyAnimator: processZyAnimator,
        zyCode: processZyCode,
        zyDE: processZyDE,
        zyCommonError: processZyCommonError,
        zyConsole: processZyConsole,
        zyYoutubeVideo: processZyYoutubeVideo,
        zyTool: processZyTool,
        zyMathWorksLearningTool: processMathWorksLearningTool,
        zyMatrix: processZyMatrix,
        table: processZyTable,
        tableRow: processTableRow,
        tableCell: processTableCell,
        tableHeader: processTableCell,
        progressionBuilder: processProgressionBuilder,
        unsupported: processUnsupported,
        unsupportedEditable: processUnsupportedEditable,
        blockOrderingBuilder: processBlockOrderingBuilder,
        streamlitApp: processStreamlitApp,
        codeWritingBuilder: processCodeWritingBuilder,
        codeOutputBuilder: processCodeOutputBuilder,
        parsonsCodingBuilder: processParsonsCodingBuilder,
        openStaxPythonCodeRunnerBuilder: processOpenStaxPythonCodeRunnerBuilder,
        zyGdocDrawing: processZyGdocDrawing,
    };

    // If the node type is not in the schema, use the others processor. If it is, use the HTML processor.
    const processor =
        processors[node.type as string] ?? (!ctx.nodeTypesInSchema.includes(node.type as string) ? processOthers : processHTML);

    const zybooksJson = await processor(node, docAttr, ctx);

    if (!options?.skipGuidGeneration) {
        // Generate GUID if not explicitly set in node attributes
        zybooksJson.guid = node.attrs?.guid ? node.attrs.guid : `${docAttr?.sectionId}_${node.attrs?.id}`;
    }

    return processCaption(zybooksJson);
};

export const convertProseMirrorToZybooksJSONCore = async (
    prosemirrorDoc: JSONContent | string,
    options: ConvertProseMirrorToZybooksJSONOptions | undefined,
    deps: ConvertProseMirrorToZybooksJSONDeps,
): Promise<{ contentResources: any[]; enumMaps: Partial<ReferenceToContentResourcesEnumMaps> }> => {
    const doc = typeof prosemirrorDoc === 'string' ? (JSON.parse(prosemirrorDoc) as JSONContent) : prosemirrorDoc;
    const chapterNumber = doc.attrs?.chapterNumber;
    const sectionNumber = doc.attrs?.sectionNumber;

    // For lab section, we remove all the content except for the first child.
    if (doc.content?.[0]?.type === 'zyStudio' && doc.content?.[0]?.attrs?.activityType === 'lab') {
        doc.content = [doc.content[0]];
    } else if (doc.content?.[0]?.type === 'zyClassicLab') {
        doc.content = [doc.content[0]];
    } else if (doc.content?.[0]?.type === 'zyITLab') {
        doc.content = [doc.content[0]];
    }

    const footnoteReferences: Record<string, any>[] = [];
    const contentResources: Record<string, any>[] = [];

    let inline_enums_lettering_map: EnumTree = {};
    let inline_enum_map: InlineEnumMap = {};

    const nodeTypesInSchema = flattenExtensions(deps.getExtensions())
        .filter(extension => extension.type === 'node')
        .map((extension: { name: string }) => extension.name);

    const ctx: ConvertContext = { deps, api: createZybooksApi(deps.servers), nodeTypesInSchema };

    const transformedDoc = transformDoc(doc, options?.selectedZyVersion ?? null, deps);
    const processedNodes = await Promise.all(
        (transformedDoc.content || []).map(node =>
            processNode(
                node,
                {
                    ...transformedDoc.attrs,
                    attrs: transformedDoc.attrs,
                    isMAPreview: options?.isMAPreview,
                    zybookCode: options?.zybookCode,
                },
                { skipGuidGeneration: options?.skipGuidGeneration },
                ctx,
            ),
        ),
    );

    for (const processed of processedNodes) {
        if (processed !== null) {
            // Extract and register enum IDs
            const enumIds = extractEnumIds(processed);
            const { tree, map } = buildEnumIdsMap(enumIds, chapterNumber, sectionNumber, inline_enums_lettering_map, inline_enum_map);
            inline_enums_lettering_map = tree;
            inline_enum_map = map;

            // Handle footnotes
            const [contentResource, ...references] = processZyFootnote(processed, {
                attrs: transformedDoc.attrs,
            });
            footnoteReferences.push(...references);
            contentResources.push(contentResource);
        }
    }

    const allResources = [...contentResources, ...footnoteReferences.sort((a, b) => (a.order || 0) - (b.order || 0))];

    return { contentResources: allResources, enumMaps: { inline_enums_lettering_map, inline_enum_map } };
};
